import React from 'react';

import { Root } from '@atlaskit/navigation-system';

import type { AnyGlobalRootQueryData, GlobalRootProps } from '../common/types';

import { AppChrome } from './AppChrome';

/**
 * GlobalRoot will _progressively_ increase in capability.
 * Initially, it will care about the Banner, TopNav and SideNav.
 *
 * The TopNav slot is backed by GlobalTopNavigation (GTN). Consumers customize
 * it by providing a `TopNavSlot` render prop and passing GTN props (such as
 * `AppHomeSlot` and `queryRef`) to the supplied `TopNav` component.
 *
 * This means that apps *still* need to co-ordinate the right-side panel themselves,
 * which means the TopNav button "Help" needs to be specially-customizable by apps,
 * to participate in the co-ordination
 */
export function GlobalRoot<Q extends AnyGlobalRootQueryData = AnyGlobalRootQueryData>(
	props: GlobalRootProps<Q>,
): React.JSX.Element {
	const {
		app,
		appLabel,
		children,
		customizationStore,
		isAnonymous = false,
		isChromeless = false,
		BannerSlot,
		SideNavSlot,
		TopNavSlot,
		useRelayQuery,
		...rootProps
	} = props;

	return (
		<Root {...rootProps}>
			{!isChromeless && (
				<AppChrome
					app={app}
					appLabel={appLabel}
					customizationStore={customizationStore}
					BannerSlot={BannerSlot}
					SideNavSlot={SideNavSlot}
					TopNavSlot={TopNavSlot}
					useRelayQuery={useRelayQuery}
				/>
			)}
			{children}
		</Root>
	);
}
